import styles from './OfferCardContent.module.scss';
import { useMemo } from 'react';
import {
  MAX_USAGE_DATASET,
  getMaxUsageFeatures,
  MAX_FEATURES,
  BUSINESS_FEATURES,
  PRO_LIMITED_FEATURES,
  PRO_EXTENDED_FEATURES,
  NEW_MAX_USAGE_DATASET,
  getNewMaxUsageFeatures,
  ADDITIONAL_SN_BUSINESS_FEATURES,
  ADDITIONAL_SN_STANDARD_FEATURES,
  ADDITIONAL_SN_FEATURES,
  NEW_SN_STANDARD_FEATURES,
  NEW_SN_PRO_FEATURES,
  NEW_SN_ULTRA_FEATURES,
  NEW_SN_MAX_FEATURES,
} from 'src/constants';
import { PlanFamily, PlanKind } from 'src/types';
import { OfferCardContentItem } from '../OfferCardContentItem/OfferCardContentItem';
import { PlanCardContentItem } from 'src/pages/PaymentBetaPage/components/PlanCardContentItem';

interface OfferCardContentProps {
  planFamily: PlanFamily;
  isOnlyKeyFeatures?: boolean;
  planKind: PlanKind;
  isTeams?: boolean;
  isNewVariant?: boolean;
}

export const OfferCardContent = ({
  planFamily,
  isOnlyKeyFeatures,
  planKind,
  isNewVariant,
}: OfferCardContentProps) => {
  const isMax = planFamily === PlanFamily.MAX;
  const isBusiness = planFamily === PlanFamily.BUSINESS;

  const usageFeatures = useMemo(() => {
    if (isMax || isBusiness) {
      const features = isNewVariant
        ? NEW_MAX_USAGE_DATASET[planKind]
        : MAX_USAGE_DATASET[planKind];

      return !!features
        ? isNewVariant
          ? getNewMaxUsageFeatures(features)
          : getMaxUsageFeatures(features)
        : undefined;
    }
  }, [isMax, planKind, isBusiness, isNewVariant]);

  const additionalFeatures = useMemo(() => {
    if (isBusiness) {
      return ADDITIONAL_SN_BUSINESS_FEATURES;
    }
    if (
      planKind === PlanKind.MAX_05_MONTHLY ||
      planKind === PlanKind.MAX_05_ANNUALLY
    ) {
      return ADDITIONAL_SN_STANDARD_FEATURES;
    } else {
      return ADDITIONAL_SN_FEATURES;
    }
  }, [planKind, isBusiness]);

  const newCommonFeatures = useMemo(() => {
    if (
      planKind === PlanKind.MAX_05_ANNUALLY ||
      planKind === PlanKind.MAX_05_MONTHLY
    ) {
      return NEW_SN_STANDARD_FEATURES;
    }

    if (
      planKind === PlanKind.MAX_10_ANNUALLY ||
      planKind === PlanKind.MAX_10_MONTHLY ||
      planKind === PlanKind.BUSINESS_10_ANNUALLY ||
      planKind === PlanKind.BUSINESS_10_MONTHLY
    ) {
      return NEW_SN_PRO_FEATURES;
    }

    if (
      planKind === PlanKind.MAX_20_ANNUALLY ||
      planKind === PlanKind.MAX_20_MONTHLY ||
      planKind === PlanKind.BUSINESS_20_ANNUALLY ||
      planKind === PlanKind.BUSINESS_20_MONTHLY
    ) {
      return NEW_SN_ULTRA_FEATURES;
    }

    if (
      planKind === PlanKind.MAX_30_ANNUALLY ||
      planKind === PlanKind.MAX_30_MONTHLY ||
      planKind === PlanKind.BUSINESS_30_ANNUALLY ||
      planKind === PlanKind.BUSINESS_30_MONTHLY
    ) {
      return NEW_SN_MAX_FEATURES;
    }

    return [];
  }, [planKind]);

  const commonFeatures = useMemo(() => {
    if (
      planKind === PlanKind.PRO_10_ANNUALLY ||
      planKind === PlanKind.PRO_10_MONTHLY
    ) {
      return PRO_LIMITED_FEATURES;
    }

    if (
      planKind === PlanKind.PRO_30_ANNUALLY ||
      planKind === PlanKind.PRO_30_MONTHLY
    ) {
      return PRO_EXTENDED_FEATURES;
    }

    if (isMax) {
      return MAX_FEATURES;
    }

    if (isBusiness) {
      return BUSINESS_FEATURES;
    }

    return [];
  }, [isBusiness, isMax, planKind]);

  const features = useMemo(() => {
    const filteredFeatures = commonFeatures.filter(
      (item) => !isOnlyKeyFeatures || !!item.isKeyFeature,
    );

    return [...(usageFeatures || []), ...filteredFeatures];
  }, [commonFeatures, isOnlyKeyFeatures, usageFeatures]);

  const newFeatures = useMemo(() => {
    return [...(usageFeatures || []), ...newCommonFeatures];
  }, [newCommonFeatures, usageFeatures]);

  return (
    <div className={styles.root}>
      {(isNewVariant ? newFeatures : features).map((item, index) => (
        <OfferCardContentItem key={index} {...item} />
      ))}
      {isNewVariant && (
        <>
          {additionalFeatures.map((item, index) => (
            <PlanCardContentItem key={index} {...item} />
          ))}
        </>
      )}
    </div>
  );
};
